import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';
import { EmployeeAvailabilityDayType, EmployeeAvailabilityType, EventsByResourceType, EventsListType, EventTooltipType, EventType, ResourceGroupType, SchedulerDraggingPlaceholder, SchedulingView, TimeBlock } from './SchedulingTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { Droppable } from 'services/dragdrop/Droppable';
import { formatDayHeaderLabel, generateDayKey, getStartOfWeek } from './functions/schedulingDateUtils';
import useAdjustSchedulerSize from './functions/useAdjustSchedulerSize';
import Event from './Event';
import { getOverlappingEvents } from './functions/calculateOverlappingEvents';
import { saveData } from 'services/api/saveData';
import { Avatar } from 'components/layout/Avatar';
import EventTooltip from './EventTooltip';
import AddJobOrActivityForm from './AddJobOrActivityForm';
import AddActivityForm from './AddActivityForm';
import RightClickMenu, { CopiedEvent, RightClickMenuType } from './RightClickMenu';
import { AppointmentEventType, TimeRowEventType } from './SchedulingTypes';

interface EmployeeSchedulerResourcesProps {
    view: SchedulingView;
    resourceGroups: ResourceGroupType[];
    days: Date[];
    hours: string[] | undefined;
    timeSlots: string[] | undefined;
    isDragging: boolean;
    draggingPlaceholder: SchedulerDraggingPlaceholder | null;
    timeBlocks: TimeBlock[];
    events: EventsListType;
    eventsByResource: EventsByResourceType;
    eventOverlapMap: Map<string, EventType[]>;
    employeeAvailability: EmployeeAvailabilityType;
    setResourceGroups: React.Dispatch<React.SetStateAction<ResourceGroupType[]>>;
    onScheduleListHeightChange: (height: number) => void;
    onInitialResizeDone: (isDone: boolean) => void;
    onViewChange: (view: SchedulingView) => void;
    onRefresh: () => void;
    onShowAppointmentOnMap?: (event: AppointmentEventType | TimeRowEventType) => void;
    onAppointmentResize?: (event: EventType, newDurationMinutes: number) => void;
    sidePanelWidthPx?: number | null;
    showSidePanel?: boolean;
}

const EmployeeSchedulerResources: React.FC<EmployeeSchedulerResourcesProps> = ({
    view, resourceGroups, days, hours, timeSlots, isDragging, draggingPlaceholder,
    timeBlocks, events, eventsByResource, eventOverlapMap, employeeAvailability, onRefresh,
    setResourceGroups, onScheduleListHeightChange, onInitialResizeDone, onViewChange,
    onShowAppointmentOnMap, onAppointmentResize, sidePanelWidthPx, showSidePanel
}) => {
    const { t } = useTranslation();
    const { isTopNavbarEnabled, setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone, environmentSettings } = useSettings();
    const { initializeModal } = useModal();
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const isSidePanelShown = showSidePanel ?? view.showJobInbox;
    const { scheduleListHeight, pixelsPerMinute, dayColumnHeight, isInitialResizeDone, isMinimumRowHeight } = useAdjustSchedulerSize(calendarRef, isTopNavbarEnabled, view.direction, view.viewType, days, timeSlots, isSidePanelShown, events, eventsByResource, resourceGroups, sidePanelWidthPx);
    const [eventTooltip, setEventTooltip] = useState<EventTooltipType>({ visible: false, x: 0, y: 0, event: null });
    const [rightClickMenu, setRightClickMenu] = useState<RightClickMenuType | null>(null);
    const [copiedEvent, setCopiedEvent] = useState<CopiedEvent | null>(null);

    // Adjust the rows height to fill the screen
    useAdjustSchedulerSize(calendarRef, isTopNavbarEnabled, view.direction, view.viewType, days, timeSlots, isSidePanelShown, events, eventsByResource, resourceGroups, sidePanelWidthPx);

    // Pass the calculated schedule list height to the parent component
    useEffect(() => {
        onScheduleListHeightChange(scheduleListHeight);
    }, [scheduleListHeight]);

    // Pass the initial resize done state to the parent component
    useEffect(() => {
        onInitialResizeDone(isInitialResizeDone);
    }, [isInitialResizeDone]);

    // Close the right click menu, if opened
    useEffect(() => {
        const handleClick = () => setRightClickMenu(null);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    // Get todays date as translated day header label
    const today = formatDayHeaderLabel(new Date());

    // Generate time slot class name, to show divider borders
    const generateTimeSlotClass = (
        timeSlot: string,
        startTime: string,
        endTime: string,
        dragOver: boolean,
        isDragging: boolean,
        hasEvent: boolean
    ): string => {
        // Set the base class
        let className = 'time-slot';

        // If the timeslot is the same as the view start- and end time, return base class
        if (timeSlot === startTime || timeSlot === endTime) {
            return className;
        }

        // Determine the timeslot class based on the minutes
        const minutes = timeSlot.split(':')[1];
        if (minutes === '30') className += ' half-hour';
        if (minutes === '00') className += ' whole-hour';

        // Add no hover if dragging over this timeslot
        if (dragOver || isDragging) className += ' no-hover';

        // Add classname when the timeslot has an event
        if (hasEvent) className += ' has-event';

        return className;
    };

    // Determines if a given timeslot falls outside the employee's working window for that day.
    // employeeAvailability is empty ({}) whenever the employee_timetable feature/setting are
    // inactive, so this is always false in that case - no separate feature check needed here.
    const isTimeSlotOutsideEmployeeWindow = (dayAvailability: EmployeeAvailabilityDayType | undefined, timeSlot: string): boolean => {
        if (!dayAvailability) return false;
        if (!dayAvailability.is_working) return true;
        if (!dayAvailability.start_time || !dayAvailability.end_time) return false;

        const toMinutes = (time: string) => {
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };

        const slotMinutes = toMinutes(timeSlot);
        return slotMinutes < toMinutes(dayAvailability.start_time) || slotMinutes >= toMinutes(dayAvailability.end_time);
    };

    // Determines the employee's roster start/end time for this resource and day, falling back to
    // the scheduling display times when the employee has no (working) roster day - used to prefill
    // the direct-schedule form when "schedule appointments for the whole day by default" is on
    const getWholeDayTimes = (resourceId: string | number | null, dayKey: string): { startTime: string; endTime: string } | null => {
        const dayAvailability = resourceId != null ? employeeAvailability[resourceId]?.[dayKey] : undefined;

        if (dayAvailability?.is_working && dayAvailability.start_time && dayAvailability.end_time) {
            return { startTime: dayAvailability.start_time.slice(0, 5), endTime: dayAvailability.end_time.slice(0, 5) };
        }

        const displayStartTime = view.showTimes?.startTime;
        const displayEndTime = view.showTimes?.endTime;
        if (displayStartTime && displayEndTime) {
            return { startTime: displayStartTime, endTime: displayEndTime };
        }

        return null;
    };

    // Check the subcontractor day passes before planning on a subcontractor row
    const { checkDayPasses } = useSubcontractorDayPassCheck();

    // Determine whether a resource row belongs to a subcontractor
    const isSubcontractorResource = (resourceId: string | number | null): boolean => {
        if (resourceId === null || resourceId === 'unassigned') return false;
        return resourceGroups.some(group => group.resources.some(resource => String(resource.id) === String(resourceId) && resource.is_subcontractor_role));
    };

    // Find the equipment id of a resource row, undefined for employee rows
    const findResourceEquipmentId = (resourceId: string | number | null): number | undefined => {
        if (resourceId === null || resourceId === 'unassigned') return undefined;
        const resource = resourceGroups.flatMap(group => group.resources).find(resource => String(resource.id) === String(resourceId));
        return resource?.resource_type === 'equipment' && resource.equipment_id ? resource.equipment_id : undefined;
    };

    // Handle add job or activity
    const handleAddJob = async (e: React.MouseEvent, resourceUserHash: string | null, resourceId: string | number | null, dayKey: string, timeSlot?: string) => {
        // Prevent default
        e.preventDefault();

        // Warn when the subcontractor of the clicked row has no day pass for this day
        if (view.createJobs && isSubcontractorResource(resourceId) && resourceUserHash) {
            const mayContinue = await checkDayPasses([{ user_hash: resourceUserHash, dates: [dayKey] }]);
            if (!mayContinue) return;
        }

        // Determine the default times from the employee roster or the scheduling settings
        let selectedTime = timeSlot;
        let selectedEndTime: string | undefined;
        const defaultTimes = getWholeDayTimes(resourceId, dayKey);

        // Prefill the start time from the roster or scheduling settings when no time slot was clicked
        if (!selectedTime && defaultTimes) {
            selectedTime = defaultTimes.startTime;
        }

        // When scheduling whole day by default is on, prefill both the start and end time with the
        // employee's roster (or the displayed scheduling times as a fallback) instead of wherever
        // was clicked, otherwise the form falls back to one hour after the start time
        if (environmentSettings.scheduling_default_appointment_whole_day && defaultTimes) {
            selectedTime = defaultTimes.startTime;
            selectedEndTime = defaultTimes.endTime;
        }

        // Configure the selected calendar position data
        const selectedCalendarPosition = {
            selectedResource: resourceUserHash,
            selectedEquipment: findResourceEquipmentId(resourceId),
            selectedDate: dayKey,
            selectedTime: selectedTime,
            selectedEndTime: selectedEndTime,
        }

        // Show either the add job or activity form
        if (view.createJobs) {
            initializeModal(
                React.cloneElement(<AddJobOrActivityForm />, { linkedItem: selectedCalendarPosition }), {
                    title: t('job.general.direct_schedule_label'),
                    modalSize: 'add-form',
                    onSuccess: () => {
                        // Trigger scheduler event refresh
                        onRefresh();
                    }
                } 
            );

        // When creating of jobs in the office is disabled, only render the add activity form
        } else {
            initializeModal(
                React.cloneElement(<AddActivityForm />, { linkedItem: selectedCalendarPosition }), {
                    title: t('scheduling.other_activity.add_activity_label'),
                    modalSize: 'add-form',
                    onSuccess: () => {
                        // Trigger scheduler event refresh
                        onRefresh();
                    }
                } 
            );
        }
    }

    // Determine the resource row classname
    const determineResourceRowClass = (isUnassignedRow: boolean, isFirstResourceInFirstGroup: boolean): string => {
        let className = isUnassignedRow ? 'unassigned-row' : 'resource-row';
    
        if (view.direction === 'stacked') {
            className += isUnassignedRow ? ` grid-${days.length}-columns` : ` adjust-height grid-${days.length}-columns`;
        } else {
            className += ' adjust-width';
        }

        // Add class to remove the border if the unassigned resource is not visible
        if (!view.showUnassignedResource && !isUnassignedRow && view.direction === 'columns' && isFirstResourceInFirstGroup) {
            className += ' no-border-left';
        }
    
        return className;
    };

    // Filter out the unassigned row from the resource groups
    const nonUnassignedGroupAmount = () => {
        const filteredGroups = resourceGroups
            // Filter out deleted resources groups
            .filter(group => !group.deleted)

            // Filter out the unassigned row
            .filter(group => group.name !== 'Unassigned')
        
        return filteredGroups.length;
    }

    // Determine the dragging placeholder style
    const determineDraggingPlaceholderStyle = (viewType: string, resourceDirection: string, durationMinutes?: number, pixelsPerMinute?: number): React.CSSProperties | undefined => {

        // Set the size of the event
        let eventSize;
        if (pixelsPerMinute && durationMinutes) {
            eventSize = durationMinutes * pixelsPerMinute;
        
        // For scheduling list view, set a hardcoded event size
        } else {
            eventSize = 1.75;
        }
    
        // For stacked direction, the events are positioned from left to right
        if (resourceDirection === 'stacked') {
            return {
                position: viewType === 'employee_timeline' ? 'absolute' : 'relative',
                top: '0px',
                left: `0px`,
                width: viewType === 'employee_timeline' ? `${eventSize}px` : undefined,
                height: viewType === 'employee_timeline' ? '99%' : `${eventSize}rem`,
                marginTop: viewType === 'employee_timeline' ? undefined : '0.25rem',
                marginLeft: viewType === 'employee_timeline' ? undefined : '0.25rem',
                marginRight: viewType === 'employee_timeline' ? undefined : '0.25rem',
                backgroundColor: '#ffd1a3',
                border: '1px dashed #df4203',
                borderRadius: '0.25rem',
                zIndex: 1000
            };
        }
    
        // For columns direction, the events are positioned from top to bottom
        if (resourceDirection === 'columns') {
            return {
                position: viewType === 'employee_timeline' ? 'absolute' : 'relative',
                top: `0px`,
                left: '0px',
                width: '99%',
                height: viewType === 'employee_timeline' ? `${eventSize}px` : `${eventSize}rem`,
                marginTop: viewType === 'employee_timeline' ? undefined : '0.25rem',
                backgroundColor: '#ffd1a3',
                border: '1px dashed #df4203',
                borderRadius: '0.25rem',
                zIndex: 1000
            };
        }
    
        return {};
    };

    // Close the right click menu
    const closeRightClickMenu = () => setRightClickMenu(null);

    // Handle the copy of an event
    const handleCopyEvent = (event: EventType) => {
        if (event.type === 'appointment') {
            setCopiedEvent({
                type: 'appointment',
                data: {
                    appointmentId: event.appointmentId,
                    durationMinutes: event.durationMinutes ?? 60,
                    startTime: event.startTime,
                    resourceId: event.resourceId,
                    dayKey: event.dayKey
                },
                lastUsedAt: Date.now()
            });
        }

        if (event.type === 'activity') {
            setCopiedEvent({
                type: 'activity',
                data: {
                    activityId: event.activityId,
                    durationMinutes: event.durationMinutes ?? 60,
                    startTime: event.startTime,
                    resourceId: event.resourceId,
                    dayKey: event.dayKey
                },
                lastUsedAt: Date.now()
            });
        }
    };

    // Move resource to reorder
    const moveResource = async (
        groupIndex: number, 
        resourceIndex: number, 
        direction: 'up' | 'down' | 'left' | 'right'
    ) => {
        const group = resourceGroups[groupIndex];
        const resources = [...group.resources];
    
        if (!resources || resources.length < 2) return;
    
        let newIndex = direction === 'up' || direction === 'left' ? resourceIndex - 1 : resourceIndex + 1;
    
        if (newIndex < 0 || newIndex >= resources.length) return;
    
        // Update the ordering of the resources
        [resources[resourceIndex].ordering, resources[newIndex].ordering] =
            [resources[newIndex].ordering, resources[resourceIndex].ordering];

        // Configure the submit data
        const submitData = resources.map(r => ({ 
            id: r.id, 
            name: r.name,
            deleted: r.deleted,
            ordering: r.ordering
        }));
    
        try {
            // Save the ordering
            await saveData({ apiUrl: 'patch_resource_batch', method: 'patch', data: submitData });

            // Update the resource group state
            setResourceGroups(prev => {
                const updatedGroups = [...prev];
                updatedGroups[groupIndex] = { ...group, resources };
                return updatedGroups;
            });

            // Show success alert
            setFloatingAlert({ 'type': 'success', 'message': 'validation.scheduling.reorder_resource_successful' })            
        } catch (error) {
            // Show error alert
            setFloatingAlert({ 'type': 'danger', 'message': 'validation.scheduling.reorder_resource_failed' })
        }
    };

    // Handle day click
    const handleDayNameClick = (day: Date) => {
        const isCurrentlyDay = view.period === 'day';
        const dateIsSame = 
            view.displayDate.toDateString() === day.toDateString();

        if (isCurrentlyDay && dateIsSame) {
            // Back to week
            onViewChange({
                ...view,
                period: 'week',
                displayDate: getStartOfWeek(day)
            });
        } else {
            // To day mode
            onViewChange({
                ...view,
                period: 'day',
                displayDate: day
            });
        }
    };

    // Show the event tooltip
    const showTooltip = (x: number, y: number, event: EventType) => {
        setEventTooltip({ visible: true, x, y, event });
    };

    // Hide the event tooltip
    const hideTooltip = () => {
        setEventTooltip(prev => ({ ...prev, visible: false }));
    };

    // Handle show right click menu
    const handleShowRightClickMenu = (e: React.MouseEvent, resourceId: string | number | null, dayKey: string, timeSlot?: string) => {
        e.preventDefault();

        setRightClickMenu({
            type: 'empty',
            x: e.clientX,
            y: e.clientY,
            resourceId,
            dayKey,
            timeSlot
        });
    };
    
    return (
        <div ref={calendarRef} 
             className='calendar old-style'>

            {/* Day names */}
            <div className={`header grid-${days.length}-columns`}>
                <div className={`name-column ${nonUnassignedGroupAmount() > 1 ? 'show-resource-group-header-div' : ''}`}></div>
                {days.map((day, index) => {
                    return (
                        <div className='day-column' 
                             key={index}
                             style={view.direction === 'columns' ? { height: dayColumnHeight || 'auto' } : undefined}>
                            <div className='date-name'
                                 onClick={() => handleDayNameClick(day)}>
                                <span className={formatDayHeaderLabel(day) === today ? 'today' : ''}>
                                    {formatDayHeaderLabel(day) === today ? (
                                        t('date_time.relative_days.today')
                                    ): (
                                        formatDayHeaderLabel(day)
                                    )}
                                </span>
                            </div>
                            {view.timeDisplayMode === 'timeslots' && hours && (
                                <div className='header-hours'>
                                    {hours.map((hour, hourIndex) => {
                                        return (
                                            <div key={hourIndex} className={`header-hour ${hourIndex === 0 ? 'invisible' : ''}`}>
                                                {hour}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Render resource groups */}
            {resourceGroups
                .filter(group => !group.deleted && group.resources.some(resource => !resource.deleted))
                .sort((a, b) => a.ordering - b.ordering)
                .map((group, groupIndex) => (

                <div key={group.id} className="resource-group">

                    {/* Render group name */}
                    {nonUnassignedGroupAmount() > 1 && (
                        view.direction === 'columns' ? (
                            // For columns view, render resource group header div for all resource groups
                            <div className={`resource-group-header ${groupIndex > 0 ? 'border' : ''}`}>
                                {group.name !== 'Unassigned' && (
                                    group.name
                                )}
                            </div>
                        ) : (group.name !== 'Unassigned' && (
                            // For stacked view, only render resource group header div for non unassigned groups
                            <div className='resource-group-header'>
                                {group.name}
                            </div>
                        ))                            
                    )}
                    
                    {/* Render resource rows */}
                    <div className='resources'>
                        {group.resources
                            .filter(resource => !resource.deleted)
                            .sort((a, b) => a.ordering - b.ordering)
                            .map((resource, resourceIndex) => {

                            // Determine if this resource row is the unassigned row
                            const isUnassigned = resource.resource_group === 'unassigned';

                            // Determine if this is the first resource in the first group
                            const isFirstResourceInFirstGroup = groupIndex === 0 && resourceIndex === 0;

                            // Set the resource id
                            const resourceId = isUnassigned ? 'unassigned' : resource.id;

                            return (
                                <div key={resourceIndex}
                                     className={determineResourceRowClass(isUnassigned, isFirstResourceInFirstGroup)}
                                     data-resource-id={resourceId}>

                                    {/* Resource name */}
                                    <div className='name-column'>
                                        {isUnassigned ? (
                                            t('scheduling.scheduling_board.unassigned_name_label')
                                        ) : (
                                            <>
                                                <div className='resource-details'>
                                                    <div>
                                                        {resource.name}
                                                        {resource.is_subcontractor_role && resource.company_name && ` (${resource.company_name})`}
                                                        {resource.pair_role && (
                                                            // Show a link icon with the pair partner as a reminder of the worker pair
                                                            <span className='pair-link tooltip-icon'>
                                                                <FontAwesomeIcon icon={faLink} />
                                                                <span className='tooltip'>{t(`scheduling.worker_pairs.linked_${resource.pair_role}_tooltip`, { name: resource.pair_partner_name })}</span>
                                                            </span>
                                                        )}
                                                        {resource.is_helper_role && (
                                                            // Show a badge when the resource user has the helper role
                                                            <span className='bijman-badge'>{t('scheduling.worker_pairs.helper_label')}</span>
                                                        )}
                                                    </div>
                                                    {resource.is_subcontractor_role && <div className='remark'>{t('subcontractor.general.role_label')}</div>}
                                                    {resource.resource_type === 'equipment' && resource.equipment_number && <div className='remark'>{resource.equipment_number}</div>}
                                                    {resource.remark && <div className='remark'>{resource.remark}</div>}
                                                </div>

                                                {/* Equipment rows show no profile picture or avatar */}
                                                {view.showProfilePictures && resource.resource_type !== 'equipment' && (
                                                    <>
                                                        {resource.image ? (
                                                            // Shrink the profile picture in compact mode so it fits the row height
                                                            <div style={{ backgroundImage: `url(${resource.image})` }}
                                                                 className={`resource-image ${isMinimumRowHeight ? 'compact' : ''}`} />
                                                        ) : (
                                                            // Match the avatar size to the profile picture size of the layout direction
                                                            <Avatar name={resource.name} size={isMinimumRowHeight ? '1.125rem' : view.direction === 'stacked' ? '1.875rem' : '1.5rem'} />
                                                        )}
                                                    </>
                                                )}

                                                {/* <div className={`order-icon tooltip-icon ${group.resources.length === 1 ? 'visibility-hidden' : ''}`}>
                                                    {view.direction === 'stacked' && (
                                                        <>
                                                            {resourceIndex > 0 && (
                                                                <>
                                                                    <FontAwesomeIcon 
                                                                        icon={faCaretUp} 
                                                                        onClick={() => moveResource(groupIndex, resourceIndex, 'up')} />
                                                                    <span className="tooltip">{t('general.reorder')}</span>
                                                                </>
                                                            )}
                                                            {resourceIndex < group.resources.length - 1 && (
                                                                <>
                                                                    <FontAwesomeIcon 
                                                                        icon={faCaretDown} 
                                                                        onClick={() => moveResource(groupIndex, resourceIndex, 'down')} />
                                                                    <span className="tooltip">{t('general.reorder')}</span>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    {view.direction === 'columns' && (
                                                        <>
                                                            {resourceIndex > 0 && (
                                                                <>
                                                                    <FontAwesomeIcon 
                                                                        icon={faCaretLeft} 
                                                                        onClick={() => moveResource(groupIndex, resourceIndex, 'left')} />
                                                                    <span className="tooltip tooltip-left">{t('general.reorder')}</span>
                                                                </>
                                                            )}
                                                            {resourceIndex < group.resources.length - 1 && (
                                                                <>
                                                                    <FontAwesomeIcon 
                                                                        icon={faCaretRight} 
                                                                        onClick={() => moveResource(groupIndex, resourceIndex, 'right')} />
                                                                    <span className="tooltip tooltip-left">{t('general.reorder')}</span>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div> */}
                                            </>
                                        )}
                                    </div>
                                
                                    {/* Render days */}
                                    {days.map((day, dayIndex) => {
                                        const dayKey = generateDayKey(day);
                                        const isFirstDayColumn = dayIndex === 0;
                                        const resourceEvents = (eventsByResource[resourceId!] || [])
                                            .map(eventId => events[eventId])
                                            .filter(event => event !== undefined);
                                        const dayEvents = resourceEvents.filter(event => event.dayKey === dayKey && !event.deleted);

                                        // Determine if the dragging is over the current day list
                                        const dragOverCurrentDayList =
                                            draggingPlaceholder?.resourceId === resourceId &&
                                            draggingPlaceholder?.day === dayKey &&
                                            view.timeDisplayMode !== 'timeslots';

                                        return (
                                            <div className='day-column' 
                                                    key={`${resource.id}-${dayKey}`}
                                                    style={view.direction === 'columns' ? { height: dayColumnHeight || 'auto' } : undefined}>
                                                {view.timeDisplayMode === 'timeslots' && timeSlots ? (
                                                    timeSlots.map((timeSlot) => {
                                                        // Determine if the dragging is over the current timeslot
                                                        const dragOverCurrentTimeSlot =
                                                            draggingPlaceholder?.resourceId === resourceId &&
                                                            draggingPlaceholder?.day === dayKey &&
                                                            draggingPlaceholder?.timeSlot === timeSlot;

                                                        // Determine if an event is rendered within the timeslot
                                                        const hasEvent = dayEvents.some(event => event.timeSlot === timeSlot && !event.deleted);

                                                        // Determine if this timeslot falls outside the employee's working hours
                                                        const dayAvailability = employeeAvailability[resourceId!]?.[dayKey];
                                                        const isOutsideEmployeeWindow = isTimeSlotOutsideEmployeeWindow(dayAvailability, timeSlot);

                                                        // Generate timeslot class name, to show divider borders
                                                        const timeSlotClass = generateTimeSlotClass(timeSlot, view.showTimes!.startTime, view.showTimes!.endTime, dragOverCurrentTimeSlot, isDragging, hasEvent);

                                                        // Append the employee-timetable class separately, so it always applies (including on the
                                                        // first/last timeslot) without disturbing the border classes generateTimeSlotClass produces.
                                                        // Only the day's first timeslot also gets the day-edge modifier, which redraws the
                                                        // previous day-column's border as a border-left on this element (see scheduling.scss for why)
                                                        const isFirstTimeSlotOfDay = timeSlot === timeSlots[0];
                                                        let finalTimeSlotClass = timeSlotClass;
                                                        if (isOutsideEmployeeWindow) {
                                                            finalTimeSlotClass += ' outside-employee-timetable';
                                                            if (isFirstTimeSlotOfDay && !isFirstDayColumn) {
                                                                finalTimeSlotClass += ' employee-timetable-day-edge';
                                                            }
                                                        }

                                                        return (
                                                            <Droppable 
                                                                droppableId={{ type: 'timeSlot', resourceId: resourceId, day: dayKey, timeSlot }} 
                                                                key={`${resource.id}-${dayKey}-${timeSlot}`}
                                                                showPlaceholder={false}>
                                                                <div
                                                                    className={`${finalTimeSlotClass}`}
                                                                    onClick={(e) => {e.preventDefault(); handleAddJob(e, resource.user_hash, resourceId, dayKey, timeSlot)}}
                                                                    onContextMenu={(e) => handleShowRightClickMenu(e, resourceId, dayKey, timeSlot)}
                                                                >

                                                                    {dragOverCurrentTimeSlot && (
                                                                        <div className={`tooltip ${view.direction === 'columns' ? 'tooltip-left' : ''}`}
                                                                             style={dragOverCurrentTimeSlot ? { visibility: 'visible' } : { visibility: 'hidden' }}>
                                                                            {timeSlot}
                                                                        </div>
                                                                    )}

                                                                    {dragOverCurrentTimeSlot && (
                                                                        <div className="dragging-placeholder" 
                                                                            style={determineDraggingPlaceholderStyle(
                                                                                view.viewType,
                                                                                view.direction,
                                                                                draggingPlaceholder.durationMinutes,
                                                                                pixelsPerMinute!,
                                                                            )}>
                                                                        </div>
                                                                    )}

                                                                    {dayEvents.filter(event => event.timeSlot === timeSlot).map((event) => (
                                                                        <Event
                                                                            key={event.key}
                                                                            event={event}
                                                                            view={view}
                                                                            currentResource={resourceId!}
                                                                            currentDay={dayKey}
                                                                            currentTimeSlot={timeSlot}
                                                                            pixelsPerMinute={pixelsPerMinute}
                                                                            resourceDirection={view.direction}
                                                                            colorMethod={view.colorMode}
                                                                            isCompactRow={isMinimumRowHeight}
                                                                            dayEndTime={view.showTimes?.endTime}
                                                                            onResize={onAppointmentResize}
                                                                            overlappingEvents={getOverlappingEvents(eventOverlapMap, String(resourceId!), dayKey, timeSlot)}
                                                                            onOpenRightClickMenu={setRightClickMenu}
                                                                            onCloseRightClickMenu={closeRightClickMenu}
                                                                            onShowTooltip={showTooltip}
                                                                            onHideTooltip={hideTooltip}
                                                                            onRefresh={onRefresh}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </Droppable>
                                                        )
                                                    })
                                                ) : (
                                                    view.timeDisplayMode === 'timeblocks' && timeBlocks?.length ? (
                                                        timeBlocks.map((timeBlock) => {
                                                            return (
                                                                <Droppable droppableId={{ 
                                                                        type: 'timeBlock', 
                                                                        resourceId: resource.id,
                                                                        day: dayKey,
                                                                        timeBlock: timeBlock.start_time
                                                                    }} 
                                                                    key={`${resource.id}-${dayKey}-${timeBlock.start_time}`}
                                                                    showPlaceholder={false}>
                                                                    <div className=''
                                                                         onClick={(e) => handleAddJob(e, resource.user_hash, resourceId, dayKey)}>
                                                                        {dayEvents.filter(event => event.startTime === timeBlock.start_time).map((event) => (
                                                                            <Event
                                                                                key={event.key}
                                                                                event={event}
                                                                                view={view}
                                                                                currentResource={resourceId!}
                                                                                currentDay={dayKey}
                                                                                // currentTimeBlock={event.startTime}
                                                                                resourceDirection={view.direction}
                                                                                colorMethod={view.colorMode}
                                                                                onOpenRightClickMenu={setRightClickMenu}
                                                                                onCloseRightClickMenu={closeRightClickMenu}
                                                                                onShowTooltip={showTooltip}
                                                                                onHideTooltip={hideTooltip}
                                                                                onRefresh={onRefresh}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </Droppable>
                                                            )
                                                        })
                                                    ) : (
                                                        <Droppable droppableId={{ type: 'dayList', resourceId: resourceId, day: dayKey }}
                                                                    key={`${resource.id}-${dayKey}`}
                                                                    showPlaceholder={false}>
                                                            <div
                                                                className='day-list'
                                                                onClick={(e) => handleAddJob(e, resource.user_hash, resourceId, dayKey)}
                                                                onContextMenu={(e) => handleShowRightClickMenu(e, resourceId, dayKey)}
                                                            >
                                                                {dayEvents.map((event) => (
                                                                    <Event 
                                                                        key={event.key}
                                                                        event={event}
                                                                        view={view}
                                                                        currentResource={resourceId!}
                                                                        currentDay={dayKey}
                                                                        resourceDirection={view.direction}
                                                                        colorMethod={view.colorMode}
                                                                        onOpenRightClickMenu={setRightClickMenu}
                                                                        onCloseRightClickMenu={closeRightClickMenu}
                                                                        onShowTooltip={showTooltip}
                                                                        onHideTooltip={hideTooltip}
                                                                        onRefresh={onRefresh}
                                                                    />
                                                                ))}
                                                                {dragOverCurrentDayList && (
                                                                    <div className="dragging-placeholder" 
                                                                        style={determineDraggingPlaceholderStyle(
                                                                            view.viewType,
                                                                            view.direction
                                                                        )}>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Droppable>
                                                    )
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div> 
            ))}
            {eventTooltip.visible && eventTooltip.event && (
                <EventTooltip
                    event={eventTooltip.event}
                    x={eventTooltip.x}
                    y={eventTooltip.y}
                />
            )}
            {rightClickMenu && (
                <RightClickMenu
                    viewType={view.viewType}
                    menu={rightClickMenu}
                    copiedEvent={copiedEvent}
                    userLocale={userLocale}
                    userTimezone={userTimezone}
                    onCopy={handleCopyEvent}
                    onClose={closeRightClickMenu}
                    onRefresh={onRefresh}
                    setCopiedEvent={setCopiedEvent}
                    onShowOnMap={onShowAppointmentOnMap}
                    isEquipmentResource={(resourceId) => findResourceEquipmentId(resourceId) !== undefined}
                />
            )}
        </div>
    )
}

export default EmployeeSchedulerResources;